import React, { useState, useEffect, useContext } from "react";
import Base from './base.jsx'; 
import { castDataType, DataType, Mode } from "../common.jsx";
import Helpers from './helpers.jsx'
import Setting from "./setting.jsx";
import Resolver from '../component_resolver.jsx'
import * as FormComponent from "../form_components.jsx";
import { Row, Col, Button } from "../../bootstrap/index.jsx";
import update from "immutability-helper";
import Tooltip from "../../common/Tooltip.jsx";
import { fetchJson } from "../../../helpers/index.js";
import { useRemoteVariableValues } from "../../../hooks/index.js";
import { FormContext } from "../form_context.jsx";

import { calculator_calculate_path } from '~/routes';

const CALCULATION_DELAY = 3000;
const NO_DELAY = 0;

export default class AdaliaMeCalculationV2 extends Base {
  static get CATEGORY() {
    return "other";
  }

  // Calculation can be done to any component
  static resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope = "") {
    let names = [];
    if (content.name) {
      names.push(repeatedFieldScope + "[" + content.name + "]");
    }
    let variables = (content.variables || []).map((v) => repeatedFieldScope + "[" + v.componentId + "]");
    return [...variables, ...names];
  }

  static get TYPE() {
    return "adalia_me_calculation_v2";
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: "calculation_default_label",
    };
  }

  static icon() {
    return <i className="fa fa-calculator"></i>;
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      label: "label",
      name: "field_" + Helpers.randomString(),
      dataType: DataType.STRING,
      formula: "",
      variables: [
        {
          id: Helpers.randomString(),
          name: "a",
        },
      ],
      prettyView: {
          hide: false,
          show_label: false
      }
    });
  }

  static addVariable(content, v = {}) {
    return update(content, {
      variables: (arr) => {
        let i = 9;
        arr.push({
          id: Helpers.randomString(),
          name: [...Array(26)].map((_) => (++i).toString(36)).join``[arr.length],
          ...v,
        });

        return arr;
      },
    });
  }

  renderView() {
    const { answers, prefillAnswers, content, mode, findComponentsCallback, projectId, pdf, repeatedFieldScope } = this.props;
    const { formula, errorValue, variables = [] } = content;

    const defaultValue = this.getAnswer();

    return (
      <div>
        <label className={this.props.content.label_hidden ? 'hidden-label': ''}>
          <span className="field-label">{this.getText('label')}</span>
          {pdf ? null : (
            <CalculationValue
              inputName={this.fullFieldName()}
              defaultValue={defaultValue}
              shouldDisplayDefaultValue={mode == Mode.VIEW}
              shouldCalculateValue={[Mode.FILL, Mode.EDIT].includes(mode)}
              shouldFetchVariableValues={mode == Mode.FILL}
              calculationDelay={mode == Mode.FILL ? CALCULATION_DELAY : NO_DELAY}
              shouldDisplayError={mode == Mode.EDIT}
              formula={formula}
              errorValue={errorValue}
              variables={variables}
              answers={answers}
              prefillAnswers={prefillAnswers}
              repeatedFieldScope={repeatedFieldScope}
              onValueChange={this.updateAnswer}
              findComponentsCallback={findComponentsCallback}
              projectId={projectId}
              createNewComponentInstance={this.createNewComponentInstance}
              setDisableSubmit={this.props.setDisableSubmit}
            />
          )}
        </label>
        {pdf ? <div>{defaultValue}</div> : null}
      </div>
    );
  }

  renderPrettyView() {
    const defaultValue = this.getAnswer();
    const { hide = false, show_label = false } = this.props.content.prettyView || {};
    return (hide ? null :
         <div>
            {show_label &&
              <label>
                  <span className="field-label">{this.getText('label')}</span>
              </label>
            }
            {defaultValue}
          </div>
    );
  }

  renderSettings() {
    return (
      <div>
        <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText("label")} onChange={this.updateSettings} />
        <Setting.Checkbox name="label_hidden" className="label-hidden-check" label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />
        <Setting.TextField
          name="formula"
          label={I18n.t('field_formula', {scope: 'fe'})}
          placeholder={I18n.t('field_formula_placeholder', {scope: 'fe'})}
          value={this.props.content.formula}
          {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
        />
        <Setting.TextField
          name="errorValue"
          label={I18n.t('field_error_value', {scope: 'fe'})}
          placeholder={I18n.t('field_error_value_placeholder', {scope: 'fe'})}
          value={this.props.content.errorValue}
          onChange={this.updateSettings}
        />
        {this.renderVariableSettings()}
        <Setting.TextField
          name="reportKey"
          label={I18n.t('field_report_key', {scope: 'fe'})}
          value={this.props.content.reportKey}
          description={I18n.t('field_report_key_description', {scope: 'fe'})}
          {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
        />
        <Setting.Checkbox
          name="containsSensitiveInformation"
          label={I18n.t('field_contains_sensitive_information', {scope: 'fe'})}
          description={I18n.t('field_contains_sensitive_information_description', {scope: 'fe'})}
          value={this.props.content.containsSensitiveInformation}
          {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
        />
        {this.renderPrettyViewSettings()}
      </div>
    );
  }

  renderVariableSettings() {
    let variableNames = [];
    let showVariableNameWarning = false;

    const variables = this.getVariables().map((v, i) => {
      if (v.name) {
        if (variableNames.includes(v.name)) {
          showVariableNameWarning = true;
        }
        variableNames.push(v.name);
      }

      return (
        <div key={v.id} className="variable-row mb-1">
          <input type="hidden" name={`variables|${i}|id`} defaultValue={v.id} />
          <div className="variable-name">
            <input
              type="text"
              name={`variables|${i}|name`}
              placeholder={I18n.t('calculation_variable_name_placeholder', {scope: 'fe'})}
              className="form-control"
              defaultValue={v.name}
              autoComplete={this.getAutoComplete()}
              {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
            />
          </div>
          <div className="variable-component pl-1">
            {!v.formId ? (
              <select name={`variables|${i}|componentId`} className="form-control" defaultValue={`${v.componentId}${v.componentSuffix || ""}`} {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})} autoComplete={this.getAutoComplete()}>
                {this.variableComponentOptions().map((o) => (
                  <option key={o[0]} value={o[0]}>
                    {o[1]}
                  </option>
                ))}
              </select>
            ) : (
              <>
                <input type="hidden" name={`variables|${i}|formId`} className="form-control" defaultValue={v.formId} />
                <input type="hidden" name={`variables|${i}|formEntity`} className="form-control" defaultValue={v.formEntity} />
                <input type="text" name={`variables|${i}|reportKey`} readOnly className="form-control" defaultValue={v.reportKey} autoComplete={this.getAutoComplete()} />
              </>
            )}
          </div>
          <div className="variable-value pl-1">
            <Tooltip title={I18n.t('calculation_variable_value_help', {scope: 'fe'})}>
              <input
                type="text"
                name={`variables|${i}|value`}
                placeholder={I18n.t('calculation_variable_value_placeholder', {scope: 'fe'})}
                className="form-control"
                defaultValue={v.value}
                onChange={this.updateSettings}
                autoComplete={this.getAutoComplete()}
              />
            </Tooltip>
          </div>
          {this.allowModifyInSafeMode &&
            <div>
              <a href="#" className="delete icon-trash" title={I18n.t('delete_variable', {scope: 'fe'})} onClick={(e) => this.deleteVariable(e, i)}></a>
            </div>
          }
        </div>
      );
    });

    return (
      <div className="setting variables">
        <label>{I18n.t('field_variables', {scope: 'fe'})}</label>
        {showVariableNameWarning ? (
          <div className="settings-warning">
            <i className="fa fa-warning"></i>
            {" " + I18n.t('duplicate_variable_name_warning', {scope: 'fe'})}
          </div>
        ) : null}
        {variables}
        {this.allowModifyInSafeMode &&
          <Row>
            <Col size="xs-12">
              <Button elemType="button" onClick={this.addVariable}>
                <i className="icon-add"></i> {I18n.t('add_variable', {scope: 'fe'})}
              </Button>
              {this.props.addVariableUrl ? (
                <Button elemType="button" className="ml-1" onClick={() => this.addFormVariable(this.props.content.id)}>
                  <i className="icon-add"></i> {I18n.t('add_variable_from_form', {scope: 'fe'})}
                </Button>
              ) : null}
            </Col>
          </Row>
        }
      </div>
    );
  }

  getVariables() {
    return (this.props.content || {}).variables || [];
  }

  variableComponentOptions() {
    let options = [[["", I18n.t('variable_report_key_placeholder', {scope: 'fe'})]]];

    if (this.props.findComponentsCallback) {
      [
        ...this.props.findComponentsCallback({
          dataType: [DataType.INTEGER, DataType.DECIMAL, DataType.DATE, DataType.STRING],
        }),
        ...this.props.findComponentsCallback({
          className: "AdaliaMEEvaluationScoreCounter",
        }),
      ].forEach((c) => {
        if (c.id != this.props.content.id) {
          const fc = this.createNewComponentInstance(c, {});
          options.push(fc.getOptionsForComponentSelect());
        }
      });
    }
    return options.flat(1);
  }

  beforeUpdateSettings(content) {
    let variables = [];

    Object.keys(content)
      .filter((k) => k.match(/^variables\|/))
      .forEach((k) => {
        let parts = k.split("|"); // variables|index|attr
        variables[parts[1]] = variables[parts[1]] || {};

        if (parts[2] == "componentId") {
          if (content[k].match("_")) {
            variables[parts[1]][parts[2]] = content[k].split("_")[0];
            variables[parts[1]].componentSuffix = `_${content[k].split("_")[1]}`;
          } else {
            variables[parts[1]].componentSuffix = null;
            variables[parts[1]][parts[2]] = content[k];
          }
        } else {
          variables[parts[1]][parts[2]] = content[k];
        }


        delete content[k];
      });

    content.variables = variables;
    content.label_hidden = castDataType(content.label_hidden, DataType.BOOLEAN);
    this.beforeUpdateSettingsPrettyView(content);
  }

  addVariable = () => {
    const content = this.constructor.addVariable(this.props.content);
    this.props.updateSettingsCallback(content.id, content);
  };

  addFormVariable = (componentId) => {
    openModalDialog({
      title: I18n.t("add_variable"),
      modalClass: "modal-lg",
      hideButtons: true,
      url: `${this.props.addVariableUrl}?component_id=${componentId}`,
    });
  };

  deleteVariable = (e, i) => {
    e.preventDefault();

    if (confirm(I18n.t('confirm_delete_variable', {scope: 'fe'}))) {
      let content = update(this.props.content, {
        variables: {
          $splice: [[i, 1]],
        },
      });

      this.props.updateSettingsCallback(content.id, content);
    }
  };
}

// Renders input with default value or calculated value
const CalculationValue = ({
  inputName,
  defaultValue,
  shouldDisplayDefaultValue,
  shouldCalculateValue,
  shouldFetchVariableValues,
  shouldDisplayError,
  calculationDelay = 0,
  formula,
  errorValue,
  variables,
  answers,
  prefillAnswers,
  repeatedFieldScope,
  onValueChange,
  findComponentsCallback,
  projectId,
  createNewComponentInstance,
  setDisableSubmit,
}) => {
  const [displayLoading, setDisplayLoading] = useState(false);
  const inputRef = React.useRef();
  const timeout = React.useRef();

  // Get calculated value and loading state from hook.
  const [value, error, loading] = useCalculatedValue({
    shouldCalculateValue,
    shouldFetchVariableValues,
    formula,
    variables,
    answers,
    prefillAnswers,
    repeatedFieldScope,
    findComponentsCallback,
    projectId,
    createNewComponentInstance,
    setDisplayLoading,
  });


  useEffect(() => {
    if (inputRef.current) {
      // Disable submits while the remote values are actually being loaded
      jQuery(inputRef.current).closest('form').find('button[type="submit"]').attr('disabled', loading);
    }
  }, [loading])


  useEffect(() => {
    if (shouldCalculateValue) {
      // Updates answers after value is changed
      onValueChange(value);
    }
  }, [value]);

  // Disables form submission when calculation is in progress
  useEffect(() => {
    if (typeof setDisableSubmit === "function") {
      setDisableSubmit(inputName, loading || displayLoading);
    }
  }, [loading, displayLoading, inputName, setDisableSubmit]);

  // Display default value, loading text, error (in EDIT mode), errorValue (in other modes), actual value, or empty string.
  let displayedValue;
  
  if (shouldDisplayDefaultValue) {
    displayedValue = defaultValue;
  } else if (loading || displayLoading) {
    displayedValue = I18n.t("loading");
  } else if (value || value === 0) {
    displayedValue = value;
  } else if (error) {
    displayedValue = shouldDisplayError ? error : errorValue;
  } else {
    displayedValue = "";
  }

  return (
    <>
      <input
        ref={inputRef}
        // Hidden input which contains the calculated value
        // It will contain the value before the loading indicator is removed
        type="hidden"
        name={inputName}
        // Ensure that value is not null or undefined
        value={value || value === 0 ? value : ""}
      />
      <input
        // Text input just for displaying value, error or loading text
        type="text"
        className="result form-control"
        // Ensure that value is not null or undefined
        value={displayedValue || displayedValue === 0 ? displayedValue : ""}
        readOnly={true}
      />
    </>
  );
};

// Calculates value from variables
const useCalculatedValue = ({
  shouldCalculateValue,
  shouldFetchVariableValues,
  formula,
  variables,
  answers,
  prefillAnswers,
  repeatedFieldScope,
  findComponentsCallback,
  projectId,
  createNewComponentInstance,
  setDisplayLoading
}) => {
  const fContext = useContext(FormContext);
  const [value, setValue] = useState();
  const [error, setError] = useState();
  const [variableValues, setVariableValues] = useState({});
  const [remoteVariableValues, loading] = useRemoteVariableValues(
    shouldFetchVariableValues,
    projectId,
    React.useMemo(() => variables.filter((v) => v.formId && v.reportKey), [variables])
  );

  const checkVariableValuePresence = () => {
    let allPresent = true;

    variables.forEach((v) => {
      const value = variableValues[v.name];
      // Quick check to detect if variable name is found in the formula
      // Doesn't actually check if variable is used in formula
      if (!value && value !== 0 && formula.includes(v.name)) {
        allPresent = false;
        return;
      }
    });

    return allPresent;
  };

  const getVariableDataTypes = () => {
    let editedVariables = {}
    variables.forEach(variable => {
      if (!('componentId' in variable)) return
      if (!(findComponentsCallback)) return
      const component = (findComponentsCallback({ id: variable.componentId })[0])
      if (!(component)) return
      if (!('dataType' in component)) return
      editedVariables[variable.name] = component.dataType
    });
    return editedVariables
  }

  const combineVariableValuesWithDataTypes = () => {
    let editedVariables = {}
    const variableDataTypes = getVariableDataTypes()

    for (const key in variableValues) {
      editedVariables[key] = {
        value: variableValues[key],
        dataType: variableDataTypes[key]
      }
    }
    return editedVariables
  }

  const updateValue = () => {
    // // Merge remotely fetched values from other forms
    // Remote values take precedence over default values.
    setDisplayLoading(true);
    fetchJson(
      calculator_calculate_path(),
      {method: 'POST', body: JSON.stringify({formula: formula, formulaVariables: combineVariableValuesWithDataTypes()}) }
    ).then(response => {
      setDisplayLoading(false);
      const result = response.result

      // Only update state if changed
      if (result != value) {
        setValue(result);
      }
    
      if (error != response.error) {
        setError(response.error);
      }
    });
  };

  useEffect(() => {
    // Update value, when variable values change
    updateValue();
  }, [variableValues, formula, shouldCalculateValue]);

  const updateVariableValues = () => {
    // If remote values are fetched, merge with them
    // If not, start with empty values to ensure old default values won't be used
    let values = { ...(shouldFetchVariableValues ? variableValues : {}) };

    variables.forEach((v) => {
      if (v.componentId) {
        if (findComponentsCallback) {
          let c = findComponentsCallback({ id: v.componentId })[0];

          if (c) {
            let cClass = Resolver.componentClassName(c.type);
            let props = {
              content: c,
              answers: answers,
              prefillAnswers: prefillAnswers,
              repeatedFieldScope: repeatedFieldScope
            };
            const fc = (new FormComponent[cClass](props))
            values[v.name] =
              typeof fc.getAggregateAnswer != "undefined"
                ? fc.getAggregateAnswer((v.componentSuffix || "").slice(1), fContext)
                : fc.getAnswer(fContext);
          }
        }
      }

      const value = values[v.name];
      // If value is empty, use default value, otherwise use answer.
      // If neither value nor default value is present, result will not be displayed
      if (!value && value !== 0) {
        values[v.name] = v.value;
      }
    });

    // If remote values are fetched, merge with them
    // If not, start with empty values to ensure old default values won't be used
    setVariableValues(
      update(shouldFetchVariableValues ? variableValues : {}, {
      $merge: values,
      })
    );
  };

  // Merge casted remote values to variableValues
  const mergeRemoteValues = () => {
    let values = {};

    Object.keys(remoteVariableValues).forEach((k) => {
      const { value, data_type } = remoteVariableValues[k];
      // Only merge if value is present
      if (value || value === 0) {
        values[k] = castDataType(value, data_type);
      }
    });

    setVariableValues(update(variableValues, { $merge: values }));
  };

  useEffect(() => {
    if (shouldFetchVariableValues) mergeRemoteValues();
  }, [remoteVariableValues]);

  useEffect(() => {
    if (shouldCalculateValue) updateVariableValues();
  }, [answers, variables, shouldCalculateValue]);

  return [value, error, loading];
};
